import {
  IonContent,
  IonIcon,
  IonButton as Button,
  useIonActionSheet,
  IonItem,
  IonLabel,
  IonChip as Chip,
  useIonRouter,
} from '@ionic/react';
import { createSelector } from '@reduxjs/toolkit';
import { calendar, filter as filterIcon, gitCompareOutline, person } from 'ionicons/icons';
import React, { Fragment, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { t } from 'ttag';

import Layout from '@components/Layout';
import InfiniteList from '@components/List';
import RoutePath from '@custom-types/RoutePath';
import { formatDateTime } from '@services/date-service';
import { Locale } from '@services/language-service';
import { generateRoute } from '@services/navigation-service';
import { getCompanyInfo } from '@state/company';
import { getEventInfo } from '@state/event';
import { getLocale } from '@state/system';
import { getPeopleId } from '@state/user';

import { useLeads } from './queries';
import { Lead } from './service';

const Title = styled.h3`
  font-weight: 700;
  margin-bottom: 20px;
  padding-left: var(--ion-padding, 16px);
`;
const Item = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 30px;
`;
const NotFoundIcon = styled(IonIcon)`
  width: 128px;
  height: 128px;
`;
const Icon = styled(IonIcon)`
  margin-right: 5px;
  font-size: 18px;
`;

const ItemDetail = styled.div`
  padding-top: 5px;
  display: flex;
  align-items: center;
  color: #92949c;
`;
const IonChip = styled(Chip)`
  background: var(--ion-color-primary);
  color: var(--ion-color-light);
  padding: 1px 7px;
  height: 20px;
`;

const ListButtons = styled.div`
  padding-left: var(--ion-padding, 16px);
`;
const IonButton = styled(Button)`
  font-size: 14px;
  --padding-start: 10px;
  --padding-end: 10px;
`;

const selector = createSelector(
  getLocale,
  getEventInfo,
  getCompanyInfo,
  getPeopleId,
  (locale, { id: eventId, name: eventName }, { id: companyId }, peopleId) => ({
    locale,
    eventId,
    eventName,
    companyId,
    peopleId,
  })
);

enum SortBy {
  Recent,
  AZ,
}
const SortByOptions = () => ({
  [SortBy.Recent]: t`Recent`,
  [SortBy.AZ]: 'A-Z',
});

enum Filters {
  ShowAll,
  CapturedByMe,
}
const FilterOptions = () => ({
  [Filters.ShowAll]: t`Show All`,
  [Filters.CapturedByMe]: t`Captured By Me`,
});

const populateItem = ({
  lead,
  locale,
  peopleId,
  onClick,
}: {
  lead: Lead;
  locale: Locale;
  peopleId: string;
  onClick: () => void;
}) => (
  <IonItem data-testid="lead-item" onClick={onClick}>
    <IonLabel>
      {`${lead.firstName} ${lead.lastName}`}

      <ItemDetail>
        <Icon icon={person} />
        {lead.capturedByPerson ? (
          <>
            {`${lead.capturedByPerson.firstName} ${lead.capturedByPerson.lastName}`}
            {lead.capturedByPerson.id === peopleId && (
              <IonChip data-testid="lead-by-me" color="primary">{t`Me`}</IonChip>
            )}
          </>
        ) : (
          <span>{t`[Deactivated Account]`}</span>
        )}
      </ItemDetail>

      <ItemDetail>
        <Icon icon={calendar} />
        {formatDateTime({ utcDate: lead.updatedAt, locale })}
      </ItemDetail>
    </IonLabel>
  </IonItem>
);

const List = () => {
  const { locale, eventId, eventName, companyId, peopleId } = useSelector(selector);
  const { push: pushRoute } = useIonRouter();
  const [present] = useIonActionSheet();
  if (!companyId || !eventId) {
    throw new Error('Company or Event not found');
  }

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState(Filters.ShowAll);
  const [sortBy, setSortBy] = useState(SortBy.Recent);

  const handleSortClick = useCallback(() => {
    present({
      header: t`Sort By`,
      buttons: [
        {
          text: SortByOptions()[SortBy.Recent],
          data: SortBy.Recent,
        },
        {
          text: SortByOptions()[SortBy.AZ],
          data: SortBy.AZ,
        },
        {
          text: t`Cancel`,
          role: 'cancel',
        },
      ],
      onWillDismiss: ({ detail: { data } }) => {
        if (data !== undefined) {
          setSortBy(data);
        }
      },
    });
  }, [present]);

  const handleFilterByClick = useCallback(() => {
    present({
      header: t`Filter By`,
      buttons: [
        {
          text: FilterOptions()[Filters.ShowAll],
          data: Filters.ShowAll,
        },
        {
          text: FilterOptions()[Filters.CapturedByMe],
          data: Filters.CapturedByMe,
        },
        {
          text: t`Cancel`,
          role: 'cancel',
        },
      ],
      onWillDismiss: ({ detail: { data } }) => {
        if (data !== undefined) {
          setFilter(data);
        }
      },
    });
  }, [present]);

  const {
    fetchNextPage,
    isFetching,
    hasNextPage = false,
    isLoading,
    data: leadPages,
  } = useLeads({
    eventId,
    companyId,
    params: {
      search,
      sort: sortBy === SortBy.Recent ? '-updated_at' : 'first_name',
      capturedByPerson: filter === Filters.CapturedByMe ? peopleId : undefined,
    },
  });

  const totalLeadsCount = leadPages?.pages[0]?.meta?.pagination?.totalItemsCount ?? 0;

  return (
    <Layout title={eventName}>
      <IonContent>
        <InfiniteList
          loadData={fetchNextPage}
          endOfList={!hasNextPage}
          onSearch={setSearch}
          isLoading={isFetching && isLoading}
          title={<Title>{t`Collected Leads (${totalLeadsCount})`}</Title>}
        >
          <ListButtons>
            <IonButton
              disabled={!totalLeadsCount}
              shape="round"
              color="light"
              onClick={handleSortClick}
            >
              <Icon icon={filterIcon} />
              {SortByOptions()[sortBy]}
            </IonButton>
            <IonButton shape="round" color="light" onClick={handleFilterByClick}>
              <Icon icon={person} />
              {filter === Filters.ShowAll ? t`All` : FilterOptions()[filter]}
            </IonButton>
          </ListButtons>
          {leadPages && (
            <>
              {leadPages?.pages?.map(({ data: leads }) =>
                leads.map(lead => (
                  <Fragment key={lead.id}>
                    {populateItem({
                      lead,
                      locale,
                      peopleId,
                      onClick: () =>
                        pushRoute(generateRoute(RoutePath.LeadEdit, { leadId: lead.id })),
                    })}
                  </Fragment>
                ))
              )}
              {totalLeadsCount === 0 && (
                <Item>
                  <NotFoundIcon size="large" icon={gitCompareOutline} color="warning" />
                  <h2>{t`No Leads`}</h2>
                  <p>{t`There are no leads collected yet.`}</p>
                </Item>
              )}
            </>
          )}
        </InfiniteList>
      </IonContent>
    </Layout>
  );
};

export default List;
